import { useEffect, useState } from 'react';
import { openLibrary } from '@versesverse/core';
import type { ApiFetch, ContentClient } from '@versesverse/core';
import type { ManifestResponse } from '@versesverse/schema/api';
import type { Bundle } from '@versesverse/schema/types';
import type { MapSkeletonArtifact } from '@versesverse/schema/artifacts';

/** Everything a first paint needs: which build, where its content is, and the map itself. */
export interface LoadedLibrary {
  manifest: ManifestResponse;
  content: ContentClient;
  /** The map — the first thing every client fetches, because everything else is fetched from it. */
  bundle: Bundle;
  /**
   * THE MAP'S OWN ARTIFACT, when the chunked path is asked for and it arrived.
   *
   * `ARCHITECTURE.md D26` moves the compilation behind the API in pieces, and `DELIVERY.md §12` runs
   * both paths side by side while the new one is proven. So this is present when the flag is on and
   * the skeleton was fetched, absent otherwise, and the map draws from whichever it has — provably
   * the same picture either way (`core/map-model.ts`).
   */
  skeleton?: MapSkeletonArtifact;
  /**
   * Why the chunked path was not used, when it was asked for and failed.
   *
   * The fallback to the bundle is deliberate — during the rollout the bundle is still authoritative,
   * so a missing skeleton must not cost a reader the map. But it is NOT silent: a deployment serving
   * a bundle and no skeleton looks exactly like one where the flag was never on, and the difference
   * matters to whoever is proving the new path. So the reason is kept, and something can assert it.
   */
  skeletonError?: unknown;
}

/**
 * The three states a load can be in, as a discriminated union rather than three booleans — so a
 * render cannot forget the error case or read a bundle that has not arrived. There is no fourth
 * "idle": the effect starts loading immediately, which is what a reader opening the page wants.
 */
export type LibraryState =
  | { status: 'loading' }
  | { status: 'ready'; library: LoadedLibrary }
  | { status: 'error'; error: unknown };

/**
 * Open the library on mount: ask the server which build is live (the one call that needs it), then
 * fetch the map from wherever the manifest says content lives — the edge in production. Re-runs if
 * the base or the fetch changes, and ignores a resolution that arrives after unmount, so a slow
 * network cannot set state on a gone component.
 *
 * `chunkedMap` additionally fetches the skeleton (D26). The two requests go out TOGETHER rather than
 * one after the other: they are independent, and item 3 of `DELIVERY.md §4.5` measured requests as
 * free up to the browser's concurrency cap — serialising them would spend a round trip for nothing.
 */
export function useLibrary(apiBase: string, fetch: ApiFetch, chunkedMap = false): LibraryState {
  const [state, setState] = useState<LibraryState>({ status: 'loading' });

  useEffect(() => {
    let live = true;
    setState({ status: 'loading' });
    void (async () => {
      const { manifest, content, compilation } = await openLibrary(apiBase, fetch);
      /*
       * The bundle's failure is the library's failure; the skeleton's is not, while both paths are
       * live. `allSettled` rather than `all` for exactly that asymmetry — `all` would reject the whole
       * load because an artifact that is not published yet was missing.
       */
      const [gotBundle, gotSkeleton] = await Promise.allSettled([
        content.bundle(),
        chunkedMap ? compilation.mapSkeleton() : Promise.resolve(undefined),
      ]);
      if (gotBundle.status === 'rejected') throw gotBundle.reason;
      const bundle = gotBundle.value;
      const skeleton = gotSkeleton.status === 'fulfilled' ? gotSkeleton.value : undefined;
      const skeletonError = gotSkeleton.status === 'rejected' ? gotSkeleton.reason : undefined;
      if (live) setState({ status: 'ready', library: { manifest, content, bundle, skeleton, skeletonError } });
    })().catch((error: unknown) => {
      if (live) setState({ status: 'error', error });
    });
    return () => { live = false; };
  }, [apiBase, fetch, chunkedMap]);

  return state;
}
